import { PixelCrop } from 'react-image-crop';

const TO_RADIANS = Math.PI / 180;

export interface CropSize {
  width: number;
  height: number;
}

interface CropedFileProps {
  image: HTMLImageElement | string;
  crop: PixelCrop | undefined;
  scale?: number;
  rotate?: number;
  canvas?: HTMLCanvasElement;
  cropSize?: CropSize;
}

function loadImage(imageSrc: string, cropSize: CropSize): Promise<HTMLImageElement> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = imageSrc;
    img.width = cropSize.width;
    img.height = cropSize.height;
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
  });
}

export async function getCropedFile({
  image,
  crop,
  canvas,
  scale = 1,
  rotate = 0,
  cropSize,
}: CropedFileProps): Promise<File | false> {
  if (typeof image === 'string') {
    if (!cropSize) {
      throw Error('cropSize must be set for image type string');
    }
    const imageSrc = image;
    image = await loadImage(imageSrc, cropSize);
  }

  const canv = canvas ? canvas : document.createElement('canvas');

  const ctx = canv.getContext('2d');
  if (!ctx) {
    throw new Error('No 2d context');
  }

  if (!crop || crop.width === 0 || crop.height === 0) {
    crop = {
      width: image.width,
      height: image.height,
      x: 0,
      y: 0,
      unit: 'px',
    };
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canv.width = Math.floor(crop.width * scaleX * pixelRatio);
  canv.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);
  ctx.restore();

  return new Promise((resolve) => {
    canv.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `${Date.now()}`);
        resolve(file);
      } else {
        resolve(false);
      }
    }, 'image/png');
  });
}
